
import React from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GridViewIcon from "@mui/icons-material/GridView";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ArticleIcon from "@mui/icons-material/Article";
import LogoutIcon from "@mui/icons-material/Logout";
import { ReactComponent as Logo } from "../../../assets/images/logo.svg.svg";
import { logout } from "../../../Redux/Slices/authSlice";
import Styles from "./SuperAdminSidebar.module.css";

const getInitials = (name = "") =>
  name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "SA";

const SuperAdminSidebar = ({
  companyCount,
  activeSection,
  onSectionChange,
  onChargesClick,
  onConfigureClick,
  onAdminsClick,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const role = useSelector((state) => state?.persistedReducer?.userType);
  const userName = useSelector((state) => state?.persistedReducer?.user?.user?.userName);
  const canAccessCharges = role === "SUPER_ADMIN";

  const handleLogout = () => {
    dispatch(logout());
    localStorage.clear();
    sessionStorage.clear();
    window.location = "/login";
  };

  const platformItems = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <GridViewIcon />,
      badge: companyCount,
      action: () => {
        navigate("/admin_dashboard");
        onSectionChange?.("dashboard");
      },
    },
    {
      id: "companies",
      label: "Companies",
      icon: <BusinessIcon />,
      action: () => onSectionChange?.("companies"),
    },
    {
      id: "charges",
      label: "Charges",
      icon: <ReceiptIcon />,
      disabled: !canAccessCharges,
      action: () => {
        if (!canAccessCharges) return;
        onChargesClick?.();
        onSectionChange?.("charges");
      },
    },
    {
      id: "escrow",
      label: "Escrow",
      icon: <AccountBalanceIcon />,
      action: () => onSectionChange?.("escrow"),
    },
  ];

  const adminItems = [
    {
      id: "configure",
      label: "Configure",
      icon: <SettingsIcon />,
      action: () => {
        onConfigureClick?.();
        onSectionChange?.("configure");
      },
    },
    {
      id: "reports",
      label: "Reports",
      icon: <AssessmentIcon />,
      action: () => onSectionChange?.("reports"),
    },
    {
      id: "auditlog",
      label: "Audit Log",
      icon: <ArticleIcon />,
      action: () => onSectionChange?.("auditlog"),
    },
  ];

  return (
    <div className={Styles.sidebar}>
      <div className={Styles.logoSection}>
        <div className={Styles.logoText}>
          <div className={Styles.brandName}>GNA Energy</div>
          <div className={Styles.brandSub}>OTC Platform</div>
        </div>
      </div>

      <div className={Styles.navSection}>
        <div className={Styles.sectionLabel}>PLATFORM</div>
        {platformItems.map((item) => (
          <div
            key={item.id}
            className={`${Styles.navItem} ${activeSection === item.id ? Styles.navItemActive : ""} ${item.disabled ? Styles.navItemDisabled : ""}`}
            onClick={item.action}
            title={item.disabled ? "Access restricted" : undefined}
          >
            <span className={Styles.navIcon}>{item.icon}</span>
            <span className={Styles.navLabel}>{item.label}</span>
            {item.badge !== undefined && item.badge !== null && (
              <span className={Styles.navBadge}>{item.badge}</span>
            )}
          </div>
        ))}
      </div>

      <div className={Styles.navSection}>
        <div className={Styles.sectionLabel}>ADMIN</div>
        {adminItems.map((item) => (
          <div
            key={item.id}
            className={`${Styles.navItem} ${activeSection === item.id ? Styles.navItemActive : ""}`}
            onClick={item.action}
          >
            <span className={Styles.navIcon}>{item.icon}</span>
            <span className={Styles.navLabel}>{item.label}</span>
          </div>
        ))}
      </div>

      <div className={Styles.sidebarFooter}>
        <div className={Styles.userRow}>
          <div className={Styles.userAvatar}>{getInitials(userName || "Super Admin")}</div>
          <div className={Styles.userInfo}>
            <div className={Styles.userDisplayName}>{userName || "super_admin"}</div>
            <div className={Styles.userRoleLabel}>
              {role === "SUPER_ADMIN" ? "GNA Administrator" : role === "MAKER" ? "GNA Maker" : role === "CHECKER" ? "GNA Checker" : "GNA Admin"}
            </div>
          </div>
        </div>
        <button className={Styles.logoutBtn} onClick={handleLogout}>
          <LogoutIcon style={{ fontSize: "14px" }} />
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default SuperAdminSidebar;
